import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { 
  TrendingUp, 
  BarChart3,
  Building2,
  ChevronRight
} from 'lucide-react'
import { tradingService } from '@/services/tradingService'
import { formatCurrency } from '@/utils'
import { OrderbookItem } from '@/types/otc'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { InfoCard, Card } from '@/components/molecules'
import { Button } from '@/components/atoms'
import { OTCScreen, PerpsScreen, PlaceOrderScreen } from './trading'
import OrderDetailsScreen from './trading/OrderDetailsScreen'

const TradingScreen = () => {
  return (
    <Routes>
      <Route path="/" element={<TradingOverview />} />
      <Route path="/otc" element={<OTCScreen />} />
      <Route path="/otc/order/:orderId" element={<OrderDetailsScreen />} />
      <Route path="/otc/place-order" element={<PlaceOrderScreen />} />
      <Route path="/perps/*" element={<PerpsScreen />} />
    </Routes>
  )
}

const TradingOverview = () => {
  const [marketData, setMarketData] = useState<any>(null)
  const [hotDeals, setHotDeals] = useState<OrderbookItem[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const loadData = async () => {
      try {
        const [market, deals] = await Promise.all([
          tradingService.getOrderbookStats(),
          tradingService.getOrderbook({ limit: 5 })
        ])
        setMarketData(market)
        setHotDeals(deals.data || [])
      } catch (error) {
        console.error('Error loading trading data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  const tradingCategories = [
    {
      id: 'otc',
      icon: TrendingUp,
      title: 'OTC Trading',
      description: 'Over-the-counter trading with institutional liquidity',
      path: '/trading/otc',
      color: 'from-blue-500 to-blue-600',
    },
    {
      id: 'perps',
      icon: BarChart3,
      title: 'Perpetuals',
      description: 'Trade perpetual futures with leverage up to 100x',
      path: '/trading/perps',
      color: 'from-green-500 to-green-600',
      isComingSoon: true,
    },
  ]

  if (loading) {
    return <PageTemplate isLoading />
  }

  return (
    <PageTemplate
      title="Trading Hub"
      subtitle="Access professional trading tools and markets"
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      {/* Market Overview */}
      <PageTemplate.Section title="Market Overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard
            title="24h Volume"
            value={formatCurrency(marketData?.totalVolume24h || 0)}
          />
          <InfoCard
            title="Active Orders"
            value={marketData?.totalOrders || 0}
          />
          <InfoCard
            title="Avg Price"
            value={formatCurrency(marketData?.avgPrice || 0)}
          />
          <InfoCard
            title="Top Projects"
            value={marketData?.topProjects?.length || 0}
          />
        </div>
      </PageTemplate.Section>

      {/* Hot Deals */}
      <PageTemplate.Section
        title="🔥 Hot Deals"
        actions={
          <Button
            variant="secondary"
            size="sm"
            rightIcon={<ChevronRight />}
            onClick={() => navigate('/trading/otc')}
          >
            View All
          </Button>
        }
      >
        <div className="space-y-3">
          {hotDeals.map((deal) => (
            <Card
              key={deal.order_id}
              className="cursor-pointer hover:bg-[var(--accent)]/50 transition-colors"
              onClick={() => navigate(`/trading/otc?order=${deal.order_id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 size={16} className="text-gray-500" />
                  <div>
                    <h3 className="font-medium text-[var(--text)]">{deal.project_name}</h3>
                    <p className="text-sm text-[var(--text)] opacity-60">{deal.deal_type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-green-500">
                    {formatCurrency(deal.offered_amount)}
                  </div>
                  <div className="text-sm text-[var(--text)] opacity-60">
                    {formatCurrency(deal.offered_fully_diluted_value)} FDV
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </PageTemplate.Section>

      {/* Trading Categories */}
      <PageTemplate.Section title="Trading Categories">
        <div className="grid gap-4 md:grid-cols-2">
          {tradingCategories.map((category) => (
            <Card
              key={category.id}
              className={`bg-gradient-to-br ${category.color} cursor-pointer hover:opacity-90 transition-opacity relative ${
                !category.isComingSoon ? 'hover:scale-105' : ''
              }`}
              onClick={() => !category.isComingSoon && navigate(category.path)}
            >
              <div className="flex items-center gap-4">
                <category.icon size={24} className="text-white" />
                <div>
                  <h3 className="font-semibold text-white">{category.title}</h3>
                  <p className="text-sm text-white/80">{category.description}</p>
                </div>
              </div>
              {category.isComingSoon && (
                <span className="absolute top-2 right-2 text-xs bg-black/50 px-2 py-1 rounded text-white">
                  Coming Soon
                </span>
              )}
            </Card>
          ))}
        </div>
      </PageTemplate.Section>
    </PageTemplate>
  )
}

export default TradingScreen 